import React from "react";
import * as R from "ramda";
import { connect } from "react-redux";
import {
  Avatar,
  Button as AntdButton,
  Table,
  Popconfirm,
  Input,
  Icon,
  Switch,
  Tooltip
} from "antd";
import { compose, withStateHandlers } from "recompose";
import Fuse from "fuse.js";
import { withTranslation } from "react-i18next";
import { useState } from "react";

import {
  deleteCompanyUser,
  updateCompanyUser
} from "../../store/actions/company";
import { getOptimizedImage } from "../../utils/images";
import Button from "../../components/Button";

import InviteUserForm from "./InviteUserForm";

import styled from "styled-components/macro";

const SearchInputWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const ConfigWrapper = styled.div`
  display: flex;
  align-items: center;
  font-size: 12px;
  /* width: 215px; */

  .switch {
    margin-right: 15px;
  }

  .highlight {
    color: ${({ theme }) => theme["@primary"]};
  }

  .icon-info {
    font-size: 16px;
    color: ${({ theme }) => theme["@gray-50"]};
    margin-left: 5px;
    cursor: pointer;
  }
`;

const filterOptions = {
  shouldSort: true,
  keys: ["name", "email"],
  threshold: 0.5
};

const deleteButtonColumn = (isAdmin, userId, deleteCompanyUser, t) => ({
  render: (_, user) =>
    // Don't show Delete button for logged in user
    isAdmin &&
    !R.includes("headadmin", R.pathOr([], ["roles"], user)) &&
    user.id !== userId && (
      <Popconfirm
        title={t("company:users.areYouSure")}
        placement="topLeft"
        okText={t("global:delete")}
        okType="alert"
        cancelText={t("global:cancel")}
        onConfirm={() => {
          deleteCompanyUser(user.id);
        }}
      >
        <AntdButton
          shape="circle"
          type="danger"
          size="large"
          icon="delete"
          loading={user.loading}
        />
      </Popconfirm>
    ),
  align: "right"
});

const columns = (
  isAdmin,
  userId,
  modifiedKey,
  setModifiedKey,
  updateCompanyUser,
  deleteCompanyUser,
  name,
  email,
  role,
  config,
  t
) =>
  R.filter(R.identity, [
    {
      dataIndex: "avatar",
      key: "avatar",
      render: avatar => (
        <Avatar
          src={getOptimizedImage(avatar)}
          shape="square"
          size="large"
          icon="user"
        />
      ),
      align: "center",
      width: 100
    },
    {
      title: name,
      dataIndex: "name",
      key: "name",
      width: "20%"
    },
    {
      title: email,
      dataIndex: "email",
      key: "email",
      width: "25%"
    },
    {
      title: role,
      dataIndex: "roles",
      key: "roles",
      width: "20%",
      render: role =>
        role.map(r => <div key={r}>{t(`catalogs:userRoles.${r}`)}</div>)
    },
    isAdmin && {
      title: config,
      dataIndex: "config",
      key: "config",
      width: "25%",
      render: (config = {}, user, index) =>
        isAdmin &&
        R.equals(user.roles, ["operator"]) && (
          <>
            <ConfigWrapper>
              <Switch
                className="switch"
                defaultChecked={config.hunting}
                onChange={value => {
                  config.hunting = value;
                  config.candidatesBase = value;
                  user.config = config;
                  setModifiedKey(`${config.hunting}-${index}`);
                  updateCompanyUser({ userId: user.id, config });
                }}
              />
              <div
                className={config.hunting ? "highlight" : ""}
                key={modifiedKey}
              >
                {t("company:users.config.enableHunting", {
                  hunting: t("opportunity:candidates.huntingCandidatesTitle"),
                  candidatesBase: t("breadcrumbs:candidatesBase")
                })}
              </div>
              <Tooltip
                placement="top"
                title={t("company:users.config.huntingTooltip", {
                  candidatesBase: t("breadcrumbs:candidatesBase")
                })}
              >
                <Icon type="question-circle" className="icon-info" />
              </Tooltip>
            </ConfigWrapper>
            <ConfigWrapper>
              <Switch
                className="switch"
                defaultChecked={config.createOpportunity !== false}
                onChange={value => {
                  config.createOpportunity = value;
                  user.config = config;
                  setModifiedKey(`${config.createOpportunity}-${index}`);
                  updateCompanyUser({ userId: user.id, config });
                }}
              />
              <div
                className={config.createOpportunity !== false ? "highlight" : ""}
                key={`${modifiedKey}-createOpportunity`}
              >
                {t("company:users.config.enableCreateOpportunity")}
              </div>
              <Tooltip
                placement="top"
                title={t("company:users.config.createOpportunityTooltip")}
              >
                <Icon type="question-circle" className="icon-info" />
              </Tooltip>
            </ConfigWrapper>
          </>
        )
    },
    deleteButtonColumn(isAdmin, userId, deleteCompanyUser, t)
  ]);

const UsersList = ({
  users,
  userId,
  isAdmin,
  updateCompanyUser,
  deleteCompanyUser,
  isInviteModalOpen,
  showInviteModal,
  hideInviteModal,
  filter,
  isHeadquarter,
  handleSearch,
  t
}) => {
  const [modifiedKey, setModifiedKey] = useState();
  const fuse = new Fuse(users, filterOptions);
  const filteredUsers = filter ? fuse.search(filter) : users;

  return (
    <div data-test="company-users-form">
      <SearchInputWrapper>
        <Input
          placeholder={t("global:search")}
          prefix={<Icon type="search" style={{ color: "rgba(0,0,0,.25)" }} />}
          onChange={handleSearch}
          value={filter}
          style={{ width: 300 }}
        />
        {isAdmin && (
          <Button icon="user-add" onClick={showInviteModal}>
            {t("company:users.invite")}
          </Button>
        )}
      </SearchInputWrapper>
      <Table
        dataSource={filteredUsers}
        columns={columns(
          isAdmin,
          userId,
          modifiedKey,
          setModifiedKey,
          updateCompanyUser,
          deleteCompanyUser,
          t("company:users.name"),
          t("company:users.email"),
          t("company:users.role"),
          t("company:users.options"),
          t
        )}
        rowKey="id"
        size="middle"
        locale={{
          emptyText: t("company:users.empty")
        }}
      />
      <InviteUserForm
        visible={isInviteModalOpen}
        isHeadquarter={isHeadquarter}
        closeModal={hideInviteModal}
      />
    </div>
  );
};

const enhacer = compose(
  withTranslation(["global", "company"]),
  connect(
    R.applySpec({
      userId: R.path(["user", "id"]),
      isAdmin: R.pipe(
        R.pathOr([], ["user", "roles"]),
        R.either(R.includes("admin"), R.includes("headadmin"))
      )
    }),
    {
      updateCompanyUser,
      deleteCompanyUser
    }
  ),
  withStateHandlers(
    {
      isInviteModalOpen: false,
      filter: ""
    },
    {
      showInviteModal: () => () => ({
        isInviteModalOpen: true
      }),
      hideInviteModal: () => () => ({
        isInviteModalOpen: false
      }),
      handleSearch: () => e => ({
        filter: e.target.value
      })
    }
  )
);

export default enhacer(UsersList);
